import React from 'react';
import { motion } from 'framer-motion';

const Services = ({ id }) => {
  const services = [
    {
      icon: "💻",
      title: "Web & Mobile Applications",
      description: "Modern, responsive web and mobile applications with scalable architecture and optimal user experiences.",
      tech: ["Frontend Development", "Mobile Development", "Cross-platform Development", "Native Development"],
      color: "#00d4ff"
    },
    {
      icon: "🚀",
      title: "Scalable Systems",
      description: "High-performance systems designed to handle massive scale with real-time processing and optimization capabilities.",
      tech: ["System Architecture", "Load Balancing", "Auto-scaling", "Performance Tuning", "Caching Strategies", "Database Optimization"],
      color: "#ff6b35"
    },
    {
      icon: "🤖",
      title: "AI Integration",
      description: "Intelligent applications with machine learning capabilities and automated decision-making.",
      tech: ["Machine Learning", "Large Language Models", "OpenAI APIs", "Natural Language Processing", "Model Deployment"],
      color: "#9c27b0"
    },
    {
      icon: "🗄️",
      title: "Data Engineering",
      description: "Robust data pipelines, analytics platforms, and real-time data processing solutions.",
      tech: ["Data Architecture", "ETL Development", "Data Warehousing", "Real-time Analytics", "Business Intelligence"],
      color: "#2196f3"
    },
    {
      icon: "⚙️",
      title: "Cloud & DevOps",
      description: "Scalable cloud infrastructure with security, CI/CD, and automated deployment pipelines.",
      tech: ["Kubernetes", "ECS Deployment", "VPC Setup", "CI/CD Pipelines", "Security Best Practices", "Monitoring & Alerting"],
      color: "#4caf50"
    },
    {
      icon: "🔧",
      title: "Backend & Microservices",
      description: "Scalable backend systems and microservices architecture with service mesh and distributed systems.",
      tech: ["Backend Development", "API Design", "Database Design", "Service Mesh", "API Gateway", "Distributed Systems"],
      color: "#ff9800"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  return (
    <section id={id} className="section">
      <div className="container">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: '4rem' }}
        >
          <h2 style={{ 
            fontSize: 'clamp(2rem, 4vw, 3rem)', 
            marginBottom: '1rem',
            color: 'var(--color-primary)'
          }}>
            Technical Expertise
          </h2>
          <p style={{ 
            fontSize: '1.2rem', 
            maxWidth: '600px', 
            margin: '0 auto',
            color: 'var(--color-secondary)'
          }}>
            Innovation embracing cutting-edge technologies and AI-driven solutions for scalable digital transformation
          </p>
        </motion.div>



        {/* Services Section */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          style={{ marginBottom: '4rem' }}
        >
          <div
            className="services-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '2rem',
              maxWidth: '100%',
              overflow: 'visible',
              padding: '1rem'
            }}
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                whileHover={{ 
                  y: -5,
                  transition: { duration: 0.3, ease: "easeOut" }
                }}
                style={{
                  background: 'var(--color-bg-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '16px',
                  padding: '2.5rem',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  minHeight: '320px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
                className="service-card-modern"
              >


                {/* Icon and Title */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '1rem',
                  marginBottom: '1.5rem'
                }}>
                  <motion.div 
                    whileHover={{ 
                      rotate: [0, -5, 5, 0],
                      scale: 1.1,
                      transition: { 
                        duration: 0.6,
                        ease: "easeInOut"
                      }
                    }}
                    animate={{
                      y: [0, -3, 0],
                      rotate: [0, 1, -1, 0]
                    }}
                    transition={{
                      y: {
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut"
                      },
                      rotate: {
                        duration: 4,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }
                    }}
                    style={{
                      fontSize: '2.2rem',
                      background: `linear-gradient(135deg, ${service.color}, ${service.color}cc)`,
                      borderRadius: '12px',
                      padding: '0.7rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                      width: '60px',
                      height: '60px',
                      zIndex: 10
                    }}
                    className="service-icon"
                  >
                    {service.icon}
                  </motion.div>
                  <h3 style={{ 
                    fontSize: '1.4rem', 
                    fontWeight: '700',
                    color: 'var(--color-primary)',
                    margin: 0,
                    lineHeight: '1.3'
                  }}>
                    {service.title}
                  </h3>
                </div>

                {/* Description */}
                <p style={{ 
                  fontSize: '1rem', 
                  lineHeight: '1.6',
                  color: 'var(--color-secondary)',
                  marginBottom: '1.5rem'
                }}>
                  {service.description}
                </p>

                                {/* Skills/Tags */}
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '0.5rem',
                  marginBottom: '1.5rem'
                }}>
                  {service.tech.map((tech, tIndex) => (
                    <motion.span 
                      key={tIndex}
                      whileHover={{ 
                        scale: 1.05,
                        transition: { duration: 0.2 }
                      }}
                      whileTap={{ 
                        scale: 0.95,
                        transition: { duration: 0.1 }
                      }}
                      style={{
                        background: 'var(--color-bg-alt)',
                        color: 'white',
                        padding: '0.3rem 0.6rem',
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        border: '1px solid var(--color-border)',
                        minWidth: 'fit-content',
                        maxWidth: '45%',
                        cursor: 'pointer',
                        userSelect: 'none',
                        position: 'relative',
                        overflow: 'hidden',
                        zIndex: 1
                      }}
                      className="skill-tag"
                    >
                      {tech}
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services; 